import P from 'prop-types';
import classNames from 'classnames';
import { useMediaQuery } from '~/app/lib/use-media-query';
import styles from './grid.module.scss';
import { componentTypes } from './lib/component-types';

import UniversalText from '../universal-text/universal-text';
import UniversalImage from '../universal-image/universal-image';
import UniversalButton from '../universal-button/universal-button';
import YouTubePlayer from '../../lib/youtube-player/youtube-player';
import Mp4VideoPlayer from '../../lib/mp4-video-player/mp4-video-player';
import InstagramPlayer from '../../lib/instagram-player/instagram-player';
import SpotifyPlayer from '../../lib/spotify-player';
import SoundCloudPlayer from '../../lib/soundcloud-player';
import TikTokPlayer from '../../lib/tiktok-player';
import UniversalAccordion from '../universal-accordion/universal-accordion';
import BainReferralBannerSC from '../bain-referral-banner-sc/bain-referral-banner-sc';
import BorderBanner from '../border-banner/border-banner';
import AdvertiserDisclosureWrapper from '../advertiser-disclosure/advertiser-disclosure';
import LottieAnimation from '../lottie-animation/lottie-animation';
import CurrentDate from '../current-date/current-date';
import UniversalBulletList from '../universal-bullet-list/universal-bullet-list';
import ThirdPartyEmbed from '../third-party-embed/third-party-embed';
import InputButton from '../input-button/input-button';
import RetirementCalculatorSC from '../retirement-calculator-sc/retirement-calculator-sc';

const ItemComponent = ({
  type,
  text,
  image,
  button,
  bulletList,
  youtube,
  mp4Video,
  instagram,
  spotify,
  soundcloud,
  tiktok,
  bainReferralBanner,
  borderBanner,
  advertiserDisclosure,
  accordion,
  lottie,
  currentDate,
  thirdPartyEmbed,
  inputButton,
  retirementCalculator,
}) => {
  const size = useMediaQuery();
  switch (type) {
    case componentTypes.TEXT:
      return <UniversalText {...text} outputSample viewPortSize={size} />;
    case componentTypes.IMAGE:
      return <UniversalImage {...image} outputSample />;
    case componentTypes.YOUTUBE:
      return <YouTubePlayer {...youtube} includeContainer />;
    case componentTypes.MP4VIDEO:
      return <Mp4VideoPlayer {...mp4Video} outputSample includeContainer />;
    case componentTypes.INSTAGRAM:
      return <InstagramPlayer {...instagram} />;
    case componentTypes.SPOTIFY:
      return <SpotifyPlayer {...spotify} outputSample includeContainer />;
    case componentTypes.SOUNDCLOUD:
      return <SoundCloudPlayer {...soundcloud} outputSample includeContainer />;
    case componentTypes.TIKTOK:
      return <TikTokPlayer {...tiktok} />;
    case componentTypes.BUTTON:
      return <UniversalButton {...button} outputSample includeContainer />;
    case componentTypes.BAINREFERRALBANNER:
      return <BainReferralBannerSC {...bainReferralBanner} />;
    case componentTypes.BORDERBANNER:
      return <BorderBanner {...borderBanner} />;
    case componentTypes.ADVERTISERDISCLOSURE:
      return <AdvertiserDisclosureWrapper {...advertiserDisclosure} />;
    case componentTypes.ACCORDION:
      return <UniversalAccordion {...accordion} />;
    case componentTypes.BULLETLIST:
      return <UniversalBulletList {...bulletList} />;
    case componentTypes.LOTTIE:
      return <LottieAnimation {...lottie} />;
    case componentTypes.CURRENTDATE:
      return <CurrentDate {...currentDate} />;
    case componentTypes.THIRDPARTYEMBED: {
      return <ThirdPartyEmbed {...thirdPartyEmbed} />;
    }
    case componentTypes.INPUTBUTTON: {
      return <InputButton {...inputButton} />;
    }
    case componentTypes.RETIREMENTCALCULATORSC: {
      return <RetirementCalculatorSC {...retirementCalculator} />;
    }
    default:
      return null;
  }
};

const ItemComponents = ({ components, outputDashedBorder, fitWidth }) => {
  const outputEmptyGridItemStyle = !components?.some((item) => 'type' in item);

  return (
    <div
      style={{ width: fitWidth ? '100%' : 'unset' }}
      className={classNames([
        { [styles.itemComponentBorder]: outputDashedBorder },
        { [styles.emptyGridItemStyle]: outputEmptyGridItemStyle },
      ])}
    >
      {components?.map((component) => (
        <ItemComponent key={component.$unique_key} {...component} />
      ))}
    </div>
  );
};

ItemComponents.propTypes = {
  components: P.array,
  outputDashedBorder: P.bool,
  fitWidth: P.bool,
};

ItemComponents.defaultProps = {
  components: [],
  outputDashedBorder: false,
};

ItemComponent.propTypes = {
  type: P.string,
  text: UniversalText.propTypes,
  image: UniversalImage.propTypes,
  button: UniversalButton.propTypes,
  bulletList: UniversalBulletList.propTypes,
  youtube: YouTubePlayer.propTypes,
  mp4Video: Mp4VideoPlayer.propTypes,
  instagram: InstagramPlayer.propTypes,
  spotify: SpotifyPlayer.propTypes,
  soundcloud: SoundCloudPlayer.propTypes,
  tiktok: TikTokPlayer.propTypes,
  bainReferralBanner: BainReferralBannerSC.propTypes,
  borderBanner: BorderBanner.propTypes,
  advertiserDisclosure: AdvertiserDisclosureWrapper.propTypes,
  accordion: UniversalAccordion.propTypes,
  lottie: LottieAnimation.propTypes,
  currentDate: CurrentDate.propTypes,
  thirdPartyEmbed: ThirdPartyEmbed.propTypes,
  inputButton: P.object,
  retirementCalculator: RetirementCalculatorSC.propTypes,
};

ItemComponent.defaultProps = {
  type: 'none',
  text: {},
  image: {},
  button: {},
  bulletList: {},
  youtube: {},
  mp4Video: {},
  instagram: {},
  spotify: {},
  soundcloud: {},
  tiktok: {},
  bainReferralBanner: {},
  advertiserDisclosure: {},
  accordion: {},
  currentDate: {},
  lottie: {},
  inputButton: {},
  retirementCalculator: {},
};

export default ItemComponents;
